<template>
  <div class="handshake-metric" :class="{ 'handshake-metric-large': size === 'large' }">
    <div
      ref="circleRef"
      :class="['circle', { skeleton: isLoading, 'is-still': !spin }]"
      :style="sphereStyle"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointerleave="onPointerLeave"
    >
      <div class="icon-3d-wrapper" :class="{ dimmed, 'is-label': !!label }" :style="innerTransformStyle">
        <span v-if="label" class="sphere-label">{{ label }}</span>
        <GamIcon v-else :name="icon" :size="iconSize" is-current />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import GamIcon from '@/views/components/GamIcon.vue';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

export interface HandshakeMetricProps {
  isLoading?: boolean;
  count?: number;
  size?: 'default' | 'large';
  dimmed?: boolean;
  /** Mark suspended in the glass. Defaults to the gambit piece. */
  icon?: GamIconName;
  /** Text in the glass, in place of the mark. */
  label?: string;
  /** The profile pendant turns on its own, and a drag turns it too. Everywhere else the glass stays put. */
  spin?: boolean;
}

const props = withDefaults(defineProps<HandshakeMetricProps>(), {
  count: 0,
  size: 'default',
  icon: GamIconName.GAMBIT,
  label: undefined,
  spin: false,
});

const iconSize = '100%';

const circleRef = ref<HTMLElement>();
const lightX = ref(30);
const lightY = ref(25);
const rotX = ref(0);
const rotY = ref(0);
const isDragging = ref(false);

const emit = defineEmits<{ tap: [] }>();

const SENSITIVITY = 0.8;
const FRICTION = 0.95;
const MIN_VELOCITY = 0.1;
const AUTO_SPIN_SPEED = 0.15;
const TAP_THRESHOLD = 5;

let lastPointerX = 0;
let lastPointerY = 0;
let startPointerX = 0;
let startPointerY = 0;
let velocityX = 0;
let velocityY = 0;
let animFrameId = 0;
let autoSpinId = 0;

function updateLight() {
  const normX = ((rotY.value % 360) + 360) % 360;
  const normY = ((-rotX.value % 360) + 360) % 360;
  lightX.value = 15 + (normX / 360) * 70;
  lightY.value = 15 + (normY / 360) * 70;
}

const sphereStyle = computed(() => ({
  '--light-x': `${lightX.value}%`,
  '--light-y': `${lightY.value}%`,
  '--shadow-x': `${100 - lightX.value}%`,
  '--shadow-y': `${100 - lightY.value}%`,
  '--glint-top': `${5 + (lightY.value / 100) * 30}%`,
  '--glint-left': `${10 + (lightX.value / 100) * 30}%`,
}));

const innerTransformStyle = computed(() => ({
  transform: `perspective(140px) rotateX(${rotX.value}deg) rotateY(${rotY.value}deg) scale(0.85)`,
}));

function stopInertia() {
  if (animFrameId) {
    cancelAnimationFrame(animFrameId);
    animFrameId = 0;
  }
}

function tickInertia() {
  velocityX *= FRICTION;
  velocityY *= FRICTION;

  if (Math.abs(velocityX) < MIN_VELOCITY && Math.abs(velocityY) < MIN_VELOCITY) {
    animFrameId = 0;
    startAutoSpin();
    return;
  }

  rotY.value += velocityX;
  rotX.value -= velocityY;
  updateLight();
  animFrameId = requestAnimationFrame(tickInertia);
}

function tickAutoSpin() {
  if (!isDragging.value && !animFrameId) {
    rotY.value += AUTO_SPIN_SPEED;
    updateLight();
  }
  autoSpinId = requestAnimationFrame(tickAutoSpin);
}

function startAutoSpin() {
  if (!autoSpinId) {
    autoSpinId = requestAnimationFrame(tickAutoSpin);
  }
}

function stopAutoSpin() {
  if (autoSpinId) {
    cancelAnimationFrame(autoSpinId);
    autoSpinId = 0;
  }
}

function onPointerDown(e: PointerEvent) {
  if (!props.spin) return;
  stopInertia();
  stopAutoSpin();
  isDragging.value = true;
  lastPointerX = e.clientX;
  lastPointerY = e.clientY;
  startPointerX = e.clientX;
  startPointerY = e.clientY;
  velocityX = 0;
  velocityY = 0;
  circleRef.value?.setPointerCapture(e.pointerId);
}

function onPointerMove(e: PointerEvent) {
  if (!isDragging.value) return;

  const dx = e.clientX - lastPointerX;
  const dy = e.clientY - lastPointerY;
  lastPointerX = e.clientX;
  lastPointerY = e.clientY;

  velocityX = dx * SENSITIVITY;
  velocityY = dy * SENSITIVITY;

  rotY.value += velocityX;
  rotX.value -= velocityY;
  updateLight();
}

function onPointerUp(e: PointerEvent) {
  isDragging.value = false;
  const dx = Math.abs(e.clientX - startPointerX);
  const dy = Math.abs(e.clientY - startPointerY);
  const wasTap = dx < TAP_THRESHOLD && dy < TAP_THRESHOLD;

  if (wasTap) {
    emit('tap');
    startAutoSpin();
  } else if (Math.abs(velocityX) > MIN_VELOCITY || Math.abs(velocityY) > MIN_VELOCITY) {
    animFrameId = requestAnimationFrame(tickInertia);
  } else {
    startAutoSpin();
  }
}

function onPointerLeave() {
  if (isDragging.value) return;
}

onMounted(() => {
  if (props.spin) startAutoSpin();
});

onBeforeUnmount(() => {
  stopInertia();
  stopAutoSpin();
});
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
@use '@/ui/css/partial';

.handshake-metric {
  display: flex;
  align-items: center;
  justify-content: center;
}

.circle {
  --light-x: 30%;
  --light-y: 25%;
  --shadow-x: 70%;
  --shadow-y: 75%;
  --glint-top: 15%;
  --glint-left: 20%;

  width: var(--sphere-size, 64px);
  height: var(--sphere-size, 64px);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  touch-action: none;
  cursor: grab;

  &.is-still {
    cursor: default;
    touch-action: auto;
  }
  background: radial-gradient(
      circle at var(--light-x) var(--light-y),
      rgba(255, 255, 255, 0.3) 0%,
      rgba(255, 255, 255, 0.03) 25%,
      transparent 50%
    ),
    radial-gradient(circle at var(--shadow-x) var(--shadow-y), rgba(0, 0, 0, 0.08) 0%, transparent 50%), transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  box-shadow:
    inset -2px -2px 8px rgba(0, 0, 0, 0.08),
    inset 2px 2px 8px rgba(255, 255, 255, 0.14),
    0px 0px 32px rgba(255, 255, 255, 0.03);
  transition:
    box-shadow var(--transition),
    transform 0.15s ease-out;
  will-change: transform;

  &::before {
    content: '';
    position: absolute;
    top: var(--glint-top);
    left: var(--glint-left);
    width: 35%;
    height: 20%;
    border-radius: 50%;
    background: radial-gradient(ellipse at center, rgba(255, 255, 255, 0.55) 0%, rgba(255, 255, 255, 0) 70%);
    transform: rotate(-30deg);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 10%;
    left: 50%;
    transform: translateX(-50%);
    width: 40%;
    height: 15%;
    border-radius: 50%;
    background: radial-gradient(ellipse at center, rgba(255, 255, 255, 0.04) 0%, transparent 70%);
    pointer-events: none;
  }

  @include hover {
    box-shadow:
      inset -2px -2px 8px rgba(0, 0, 0, 0.06),
      inset 2px 2px 10px rgba(255, 255, 255, 0.18),
      0px 0px 48px rgba(255, 255, 255, 0.05);
  }

  &:active {
    cursor: grabbing;
  }
}

.icon-3d-wrapper {
  position: relative;
  z-index: 1;
  width: 40%;
  height: 40%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    transform 0.15s ease-out,
    opacity 0.2s ease,
    filter 0.2s ease;
  will-change: transform;
  filter: drop-shadow(0 2px 6px rgba(255, 255, 255, 0.2));
  color: var(--color-white-70);

  &.is-label {
    width: auto;
    height: auto;
    max-width: 78%;
  }

  &.dimmed {
    opacity: 0.2;
    filter: grayscale(100%) drop-shadow(0 2px 6px rgba(255, 255, 255, 0.05));
  }
}

.sphere-label {
  color: var(--color-white);
  font-size: 15px;
  font-weight: 700;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.03em;
}
</style>
